// src/components/chat/Message.jsx
import React from 'react';
import { formatTime, formatFileSize} from '../../utils/formatters';
import { getUserColor } from '../../utils/userHelpers';
import { getFileIcon, truncateFileName } from '../../utils/fileHelpers';
import { getMessageStatus } from '../../utils/messageHelpers';
import FileAttachment from '../media/FileAttachment';
import FileViewer from '../media/FileViewer';
import WaveformPlayer from '../media/WaveformPlayer';
import ImageGallery from '../media/ImageGallery';
import LinkPreview from './LinkPreview';
import './Message.css';

const Message = ({
    msg,
    user,
    currentChat,    
    onContextMenu,
    onGoToOriginal,
    onReplyClick,
    onOpenGallery,  
    onDownloadFile,
    onPlayStateChange,
}) => {
    if (!user) return null;

    const isOutgoing = msg.is_outgoing !== undefined 
        ? msg.is_outgoing 
        : (msg.from_id === user?.id);
    const isGroupChat = currentChat?.type === 'group';
    const userColor = getUserColor(msg.from_id);

    // ============ УДАЛЁННОЕ СООБЩЕНИЕ ============
    if (msg.is_deleted) {
        return (
            <div 
                className={`message deleted ${isOutgoing ? 'outgoing' : 'incoming'}`}
            >
                <div className="message-content deleted-message">
                    <i className="bi bi-trash"></i> {msg.content}
                </div>
                <div className="message-footer">
                    <span className="message-time">{formatTime(msg.created_at)}</span>
                </div>
            </div>
        );
    }
     // ============ СТАТУС ============
    let statusIcon = '';
    let statusColor = '#999999';
    if (isOutgoing) {
        const status = getMessageStatus(msg);
        statusIcon = status.icon;
        statusColor = status.color;
    }


    // ============ ОБЫЧНОЕ СООБЩЕНИЕ ============
    return (
        <div 
            id={`message-${msg.id}`} 
            className={`message ${isOutgoing ? 'outgoing' : 'incoming'}`}
            onContextMenu={(e) => onContextMenu?.(e, msg)}
            onTouchStart={(e) => {
                const timer = setTimeout(() => {
                    onContextMenu?.(e, msg);
                }, 600);
                e.currentTarget.addEventListener('touchend', () => clearTimeout(timer), { once: true });
            }}
        >
            {/* Автор в групповом чате */}
            {!isOutgoing && isGroupChat && (
                <div 
                    className="message-author" 
                    style={{ color: userColor }}
                >
                    {msg.display_name || msg.from_username || `User ${msg.from_id}`}
                </div>
            )}

            {/* Пересылаемое сообщение */}
            {msg.is_forwarded && msg.forward_from && (
                <div className="message-forward">
                    <div
                        className="forward-header"
                        onClick={() => msg.original_message && onGoToOriginal?.(msg.original_message)}
                        style={{ cursor: 'pointer' }}
                    >
                        <i className="bi-reply-fill"></i> {
                            msg.forward_from.chat_type === "group" && msg.forward_from.chat_title
                                ? `из: ${msg.forward_from.chat_title} от: ${msg.forward_from.display_name || msg.forward_from.username}`
                                : `от: ${msg.forward_from.display_name || msg.forward_from.username}`
                        }
                    </div>
                    <div className="forward-content">
                        {msg.original_message ? (
                            <>
                                {msg.original_message.content && msg.original_message.content.trim() !== '' && msg.original_message.type !== 'sticker' && (
                                    <div className="forward-text">{msg.original_message.content}</div>
                                )}
                                {msg.original_message.attachments && msg.original_message.attachments.length > 0 && (
                                    <div className="forward-attachments">
                                        <AttachmentsList
                                            attachments={msg.original_message.attachments}
                                            msgId={msg.id}
                                            onOpenGallery={onOpenGallery}
                                            onDownloadFile={onDownloadFile}
                                            onPlayStateChange={onPlayStateChange}
                                            isForward={true}
                                        />
                                    </div>
                                )}
                                {msg.original_message.type === 'sticker' && (
                                    <div className="forward-sticker">
                                        <FileAttachment mediaUrl={msg.original_message.content} />
                                    </div>
                                )}
                            </>
                        ) : (
                            <span className="forward-placeholder">(оригинальное сообщение)</span>
                        )}
                    </div>
                </div>
            )}

            {/* Ответ на сообщение */}
            {msg.reply_to && (
                <div className="message-reply" onClick={() => onReplyClick?.(msg.reply_to.id)}>
                    <div className="message-reply-header" style={{ color: getUserColor(msg.reply_to.from_id) }}>
                        <i className="bi-reply-fill"></i> Ответ для {msg.reply_to.display_name}:
                    </div>
                    <div className="message-reply-body">
                        {msg.reply_to.type === 'sticker' && (
                            <div className="reply-sticker">
                                <FileAttachment mediaUrl={msg.reply_to.content} />
                            </div>
                        )}
                        {msg.reply_to.attachments && msg.reply_to.attachments.length > 0 && (
                            <div className="message-reply-preview">
                                {msg.reply_to.attachments.slice(0, 1).map((att, idx) => {
                                    if (att.file_type === 'image') {
                                        return (
                                            <FileAttachment
                                                key={idx}
                                                mediaUrl={att.media_url} 
                                                fileName={att.fileName}                                               
                                                className="message-reply-img"
                                            />
                                        );
                                    } else if (att.file_type === 'video') {
                                        return (
                                            <FileViewer
                                                key={idx}
                                                filePath={att.media_url}
                                                fileName={att.fileName}                                                
                                                fileType="video"
                                                className="message-reply-video"
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        );
                                    } else if (att.file_type === 'audio' || /\.(mp3|wav|ogg|m4a|webm)$/i.test(att.file_name || '')) {
                                        return (
                                            <div key={idx} className="reply-preview-icon">
                                                 <i className={getFileIcon(att.file_name)}></i>
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <div key={idx} className="reply-preview-icon">
                                                 <i className={getFileIcon(att.file_name)}></i>
                                            </div>
                                        );
                                    }
                                })}
                                {msg.reply_to.attachments.length > 1 && (
                                    <div className="reply-preview-more">
                                        +{msg.reply_to.attachments.length - 1}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="reply-text-content">
                            {msg.reply_to.type !== 'sticker' && msg.reply_to.content && msg.reply_to.content.trim() !== '' ? (
                                <div className="reply-text">{msg.reply_to.content.substring(0, 80)}</div>
                            ) : (
                                msg.reply_to.attachments && 
                                msg.reply_to.attachments.length > 0 && 
                                !['image', 'video','audio'].includes(msg.reply_to.attachments[0].file_type) && (
                                    <div className="reply-text">
                                        {msg.reply_to.attachments[0].file_name}
                                        ({formatFileSize(msg.reply_to.attachments[0].file_size)})
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Текст */}
            {msg.type === 'text' && (
                <div className="message-content">
                    {renderTextWithLinks(msg.content, msg.link_previews?.length > 0)}
                </div>
            )}

            {/* Превью ссылок */}
            {msg.link_previews && msg.link_previews.length > 0 && (
                <div className="message-link-previews">
                    {msg.link_previews.map((preview, idx) => (
                        <LinkPreview key={idx} url={preview.url} preview={preview} />
                    ))}
                </div>
            )}

            {/* Вложения */}
            {msg.attachments && msg.attachments.length > 0 && msg.type !== 'voice' && (
                <div className="message-attachments">
                    <AttachmentsList
                        attachments={msg.attachments}
                        msgId={msg.id}
                        onOpenGallery={onOpenGallery}
                        onDownloadFile={onDownloadFile}
                        onPlayStateChange={onPlayStateChange}
                    />
                    {msg.content && <div className="message-content">{msg.content}</div>}
                </div>
            )}

            {/* Голосовое */}
            {msg.type === 'voice' && msg.attachments && msg.attachments.length > 0 && (
                <div className="message-voice">
                    <WaveformPlayer
                        playerId={`audio-${msg.id}-0`}
                        audioSrc={msg.attachments[0].media_url}
                        fileName={msg.attachments[0].file_name}
                        name={msg.attachments[0].file_name}
                        downloadEnabled={true}
                        onPlayStateChange={(isPlaying) => onPlayStateChange?.(msg.id, isPlaying)}
                    />
                </div>
            )}

            {/* Стикер (не пересылка) */}
            {msg.type === 'sticker' && !msg.is_forwarded && !msg.forward_from && (
                <div className="message-sticker">
                    <FileAttachment mediaUrl={msg.content} />
                </div>
            )}

            {/* Футер */}
            <div className="message-footer">
                {msg.edited_at && <span className="message-edited"> ред. </span>}
                <span className="message-time">{formatTime(msg.created_at)}</span>
                {isOutgoing && (
                    <span className="message-status" style={{ color: statusColor }}>
                        {statusIcon}
                    </span>
                )}
            </div>

        </div>
    );
};

// ============ ВСПОМОГАТЕЛЬНЫЕ КОМПОНЕНТЫ ============

// Рендер текста с кликабельными ссылками
const renderTextWithLinks = (text, hasPreviews) => {
    if (!text) return '';
    if (hasPreviews) {
        // Убираем ссылки если есть превью
        return text.replace(/(https?:\/\/[^\s]+)/g, '').trim();
    }
    return text.split(/(https?:\/\/[^\s]+)/g).map((part, idx) => {
        if (part.match(/^https?:\/\//)) {
            return (
                <a 
                    key={idx} 
                    href={part} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="message-link"
                >
                    {part}
                </a>
            );
        }
        return part;
    });
};

// Список вложений (изображения + файлы)
const AttachmentsList = ({ attachments, msgId, onOpenGallery, onDownloadFile, onPlayStateChange, isForward = false }) => {
    const images = attachments.filter(att => att.file_type === 'image');
    const otherFiles = attachments.filter(att => att.file_type !== 'image');

    return (
        <>
            {images.length > 0 && (
                <ImageGallery
                    images={images}
                    maxDisplay={6}
                    onImageClick={(imgs, index) => onOpenGallery?.(imgs, index)}
                />
            )}
            {otherFiles.length > 0 && (
                <div className="other-files">
                    {otherFiles.map((att, idx) => {
                        const fileIcon = getFileIcon(att.file_name);
                        const fileSize = formatFileSize(att.file_size);
                        const displayName = truncateFileName(att.file_name, isForward ? 30 : 20);
                        const playerId = `audio-${msgId}-${idx}`;

                        if (att.file_type === 'video') {
                            return (
                                <FileViewer
                                    key={idx}
                                    filePath={att.media_url}
                                    fileName={att.file_name}
                                    fileType="video"
                                    className={isForward ? 'forward-video' : 'attachment-video'}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            );
                        } else if (att.file_type === 'audio' || /\.(mp3|wav|ogg|m4a|webm)$/i.test(att.file_name || '')) {
                            return (
                                <WaveformPlayer
                                    key={playerId}
                                    playerId={playerId}
                                    audioSrc={att.media_url}
                                    fileName={att.file_name}
                                    name={att.file_name}
                                    downloadEnabled={true}
                                    onPlayStateChange={(isPlaying) => onPlayStateChange?.(playerId, isPlaying)}
                                />
                            );
                        } else {
                            return (
                                <div
                                    key={idx}
                                    className={isForward ? 'forward-file-card' : 'file-card'}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <div className={isForward ? 'forward-file-icon' : 'file-icon'}>
                                        <i className={fileIcon}></i>
                                    </div>
                                    <div className={isForward ? 'forward-file-info' : 'file-info'}>
                                        <div className={isForward ? 'forward-file-name' : 'file-name'} title={att.file_name}>
                                            {displayName}
                                        </div>
                                        <button onClick={() => onDownloadFile?.(att)} className="file-download">
                                            <i className="bi-download"></i> Скачать • {fileSize}
                                        </button>
                                    </div>
                                </div>
                            );
                        }
                    })}
                </div>
            )}
        </>
    );
};


export default Message;